import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useRegistrations } from '../context/RegistrationsContext';
import { CATEGORIES, CAT_STYLE } from '../categories';
import { useT } from '../i18n';
import { CalendarPrompt, openInAppleCalendar } from '../components/CalendarUtils';

const CARD_GRADIENTS = [
  'linear-gradient(135deg, #0d1a1c 0%, #0a1520 40%, #111318 100%)',
  'linear-gradient(135deg, #141a0a 0%, #0e1a0e 40%, #111318 100%)',
  'linear-gradient(135deg, #1a0d0d 0%, #1a0e10 40%, #111318 100%)',
  'linear-gradient(135deg, #0d1020 0%, #0a0d1a 40%, #111318 100%)',
];

// ── Rennklassen for import filter ──────────────────────────────────────────────
// Keywords support a trailing '*' wildcard = prefix match (e.g. 'elite*' matches 'Elite-Amateure').
// Without '*' it's a substring match anywhere in the class name.
// gender: 'm' = only matches male class names, 'f' = only female, null = both.
const RENNKLASSEN_OPTIONS = [
  // ── Männer ──────────────────────────────────────────────────────────────────
  { id: 'elite',        label: 'Elite',              gender: 'm', keywords: ['elite*'] },
  { id: 'u23',          label: 'U23',                gender: 'm', keywords: ['u23*'] },
  { id: 'amateur',      label: 'Amateure / CT',      gender: 'm', keywords: ['amateur*', 'ct und', 'ct-'] },
  { id: 'junioren',     label: 'Junioren U19',       gender: 'm', keywords: ['junior*'] },
  { id: 'jugend',       label: 'Jugend U17',         gender: 'm', keywords: ['jugend*'] },
  { id: 'schueler15',   label: 'Schüler U15',        gender: 'm', keywords: ['u15'] },
  { id: 'schueler13',   label: 'Schüler U13',        gender: 'm', keywords: ['u13'] },
  { id: 'schueler11',   label: 'Schüler U11',        gender: 'm', keywords: ['u11'] },
  { id: 'masters1',     label: 'Masters 1',          gender: 'm', keywords: ['masters 1'] },
  { id: 'masters23',    label: 'Masters 2/3',        gender: 'm', keywords: ['masters 2', 'masters 3'] },
  { id: 'masters4',     label: 'Masters 4',          gender: 'm', keywords: ['masters 4'] },
  { id: 'hobby',        label: 'Hobby',              gender: null, keywords: ['hobby*'] },
  // ── Frauen ──────────────────────────────────────────────────────────────────
  { id: 'elite_f',      label: 'Elite Frauen',       gender: 'f', keywords: ['elite*', 'frauen*', 'damen*'] },
  { id: 'u23_f',        label: 'U23 Frauen',         gender: 'f', keywords: ['u23*'] },
  { id: 'junioren_f',   label: 'Juniorinnen U19',    gender: 'f', keywords: ['junior*'] },
  { id: 'jugend_f',     label: 'Jugend U17 Frauen',  gender: 'f', keywords: ['jugend*'] },
  { id: 'schueler15_f', label: 'Schülerinnen U15',   gender: 'f', keywords: ['u15'] },
  { id: 'schueler13_f', label: 'Schülerinnen U13',   gender: 'f', keywords: ['u13'] },
  { id: 'schueler11_f', label: 'Schülerinnen U11',   gender: 'f', keywords: ['u11'] },
  { id: 'masters_f',    label: 'Masters Frauen',     gender: 'f', keywords: ['master*'] },
];

// Maps driver categories (from categories.js) to RENNKLASSEN_OPTIONS ids
const CATEGORY_TO_KLASSE = {
  // Männer
  'Elite':             'elite',
  'U23':               'u23',
  'Amateure':          'amateur',
  'U19':               'junioren',
  'U17':               'jugend',
  'U15':               'schueler15',
  'U13':               'schueler13',
  'U11':               'schueler11',
  'Master 1':          'masters1',
  'Master 2':          'masters23',
  'Master 3':          'masters23',
  'Master 4':          'masters4',
  // Frauen
  'Elite Frauen':      'elite_f',
  'U23 Frauen':        'u23_f',
  'U19 Juniorinnen':   'junioren_f',
  'U17 Juniorinnen':   'jugend_f',
  'U15 Juniorinnen':   'schueler15_f',
  'Masters Frauen':    'masters_f',
};

// Derives the relevant Rennklassen IDs from the team's driver list
function getTeamKlassenIds(drivers) {
  const ids = new Set();
  for (const d of drivers) {
    const kid = CATEGORY_TO_KLASSE[d.category || 'Elite'];
    if (kid) ids.add(kid);
  }
  return ids;
}

// Returns future events filtered by selected Rennklassen (OR logic), each tagged with _origIdx
function getFilteredImportEvents(events, klassen) {
  const todayStr = new Date().toISOString().slice(0, 10); // "2026-07-12"

  // Always: only future events
  const indexed = events
    .map((ev, idx) => ({ ...ev, _origIdx: idx }))
    .filter(ev => !ev.date || ev.date >= todayStr);

  if (klassen.size === 0) return indexed;

  // Check if any event has class data (enriched endpoint was used)
  const hasClassData = indexed.some(ev => Array.isArray(ev.raceClasses) && ev.raceClasses.length > 0);

  // If class data is missing (basic endpoint), skip filter and show all future events
  if (!hasClassData) return indexed;

  const klassenIds = [...klassen];
  return indexed.filter(ev =>
    (ev.raceClasses || []).some(rc => rcMatchesKlassen(rc, klassenIds))
  );
}

// Returns true if the class name signals a women's race class.
// Detects: "Frauen", "Damen", "Juniorinnen", "Schülerinnen", etc.
function isFemaleClass(name) {
  return /frauen|damen|innen/.test(name);
}

// Checks a single keyword against a lowercased class name.
// Trailing '*' = prefix match; otherwise substring match.
function kwMatches(name, kw) {
  const k = kw.toLowerCase();
  return k.endsWith('*') ? name.startsWith(k.slice(0, -1)) : name.includes(k);
}

// Check if a raceClass object/string matches any of the selected Rennklassen IDs.
// Gender is always enforced: male options never match female class names and vice versa.
function rcMatchesKlassen(rc, klassenIds) {
  const name = (typeof rc === 'string' ? rc : rc.className || '').toLowerCase();
  const female = isFemaleClass(name);
  return klassenIds.some(klassId => {
    const opt = RENNKLASSEN_OPTIONS.find(o => o.id === klassId);
    if (!opt) return false;
    if (opt.gender === 'm' && female) return false;   // male option, female class → skip
    if (opt.gender === 'f' && !female) return false;  // female option, male class → skip
    return opt.keywords.some(kw => kwMatches(name, kw));
  });
}

const EMPTY_FORM = { name: '', date: '', location: '', start_address: '', track_type: '', track_length: '', description: '', url: '', is_mandatory: false, is_cancelled: false };
const EMPTY_CATS = Object.fromEntries(CATEGORIES.map(c => [c, { enabled: false, start_time: '' }]));

/* ─── Category toggle editor (admin form) ─────────────────────── */
function CategoryEditor({ cats, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {CATEGORIES.map((cat) => {
        const s = CAT_STYLE[cat];
        const enabled = cats[cat]?.enabled || false;
        return (
          <div key={cat} style={{ border: `1px solid ${enabled ? s.border : 'rgba(59,73,75,0.25)'}`, background: enabled ? s.bg : 'transparent', padding: '10px 14px', transition: 'all 0.15s' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                <div onClick={() => onChange(cat, 'enabled', !enabled)} style={{ width: 32, height: 18, borderRadius: 9, background: enabled ? s.color : 'rgba(59,73,75,0.4)', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                  <div style={{ position: 'absolute', top: 3, left: enabled ? 16 : 3, width: 12, height: 12, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                </div>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: enabled ? s.color : 'var(--outline)', textTransform: 'uppercase' }}>{cat}</span>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: enabled ? 'var(--outline)' : 'var(--outline-variant)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>START</span>
                <input type="time" value={cats[cat]?.start_time || ''} disabled={!enabled} onChange={(e) => onChange(cat, 'start_time', e.target.value)}
                  style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, background: 'transparent', border: 'none', borderBottom: `1px solid ${enabled ? s.border : 'rgba(59,73,75,0.2)'}`, color: enabled ? s.color : 'var(--outline-variant)', outline: 'none', padding: '2px 4px', width: 90, opacity: enabled ? 1 : 0.4, cursor: enabled ? 'text' : 'not-allowed' }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Shared event card used on both mobile + desktop ────────── */
function EventCard({ ev, idx, isNext, isAdmin, registrations, allDrivers, onEdit, onDelete, onCalendar, driverId, driverCategory, token, onRefresh }) {
  const navigate = useNavigate();
  const regs = registrations[ev.id] || [];
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const todayEnd   = new Date(); todayEnd.setHours(23, 59, 59, 999);
  const evDate     = new Date(ev.date);
  const isToday    = evDate >= todayStart && evDate <= todayEnd;
  const isPast     = !isToday && evDate < todayStart;

  const ownReg = regs.find(r => r.driver_id === driverId);
  const isIn   = ownReg?.status === 'participating' || ownReg?.status === 'assigned';
  const isOut  = ownReg?.status === 'not_participating';
  const [regPending, setRegPending] = useState(false);
  const [showCalPrompt, setShowCalPrompt] = useState(false);

  const handleRegToggle = async (e) => {
    e.stopPropagation();
    if (!driverId || isPast) return;
    setRegPending(true);
    try {
      const newStatus = isIn ? 'not_participating' : 'participating';
      await axios.post('/api/registrations', { event_id: ev.id, driver_id: driverId, status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      await onRefresh?.();
      if (newStatus === 'participating') setShowCalPrompt(true);
    } catch (err) { console.error(err); }
    setRegPending(false);
  };

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();

  const isMandatory  = !!ev.is_mandatory;
  const isCancelled  = !!ev.is_cancelled;

  return (
    <div
      onClick={() => navigate(`/events/${ev.id}`)}
      style={{
        background: isCancelled ? 'rgba(24,6,6,0.9)' : isMandatory ? 'rgba(22,16,8,0.9)' : 'rgba(17,19,24,0.8)',
        border: `1px solid ${isCancelled ? 'rgba(220,50,50,0.45)' : isMandatory ? 'rgba(255,160,50,0.45)' : isNext ? 'rgba(var(--brand-primary-rgb),0.35)' : 'rgba(59,73,75,0.3)'}`,
        boxShadow: isCancelled ? 'inset 0 0 14px rgba(200,40,40,0.08)' : isMandatory ? 'inset 0 0 14px rgba(255,140,0,0.07)' : isNext ? 'inset 0 0 12px rgba(var(--brand-primary-rgb),0.06)' : 'none',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'border-color 0.2s',
        position: 'relative',
      }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = isMandatory ? 'rgba(255,160,50,0.7)' : isNext ? 'rgba(var(--brand-primary-rgb),0.6)' : 'rgba(var(--brand-primary-rgb),0.3)'}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = isMandatory ? 'rgba(255,160,50,0.45)' : isNext ? 'rgba(var(--brand-primary-rgb),0.35)' : 'rgba(59,73,75,0.3)'}
    >
      {/* Card header image area */}
      <div style={{ height: 120, background: isMandatory ? 'linear-gradient(135deg, #1a0e00 0%, #120c00 40%, #111318 100%)' : CARD_GRADIENTS[idx % CARD_GRADIENTS.length], position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'flex-end', padding: '0.875rem 1rem', flexShrink: 0 }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 39px, ${isCancelled ? 'rgba(200,40,40,0.04)' : isMandatory ? 'rgba(255,140,0,0.04)' : 'rgba(var(--brand-primary-rgb),0.04)'} 40px)`, pointerEvents: 'none' }} />
        {/* Top-left badges */}
        <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 5, zIndex: 2 }}>
          {isCancelled && (
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', background: '#dc2626', color: '#fff', padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
              ✕ ABGESAGT
            </div>
          )}
          {isMandatory && !isCancelled && (
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', background: '#ff9a1f', color: '#1a0800', padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 9 }}>!</span> PFLICHT
            </div>
          )}
          {isNext && (
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', background: 'var(--brand-primary)', color: '#002022', padding: '3px 8px' }}>
              NEXT
            </div>
          )}
          {ev.track_type && (
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', border: '1px solid rgba(59,73,75,0.6)', color: '#849495', padding: '3px 8px', background: 'rgba(17,19,24,0.7)' }}>
              {ev.track_type.toUpperCase()}
            </div>
          )}
        </div>
        {(isPast || isToday) && (
          <div style={{ position: 'absolute', top: 10, right: 10, fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', background: isToday ? 'rgba(var(--brand-primary-rgb),0.15)' : 'rgba(255,180,171,0.15)', color: isToday ? 'var(--brand-primary)' : '#ffb4ab', border: `1px solid ${isToday ? 'rgba(var(--brand-primary-rgb),0.35)' : 'rgba(255,180,171,0.3)'}`, padding: '3px 8px', zIndex: 2 }}>
            {isToday ? '● LIVE' : 'PAST'}
          </div>
        )}
        <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(var(--brand-primary-rgb),0.7)', marginBottom: 4 }}>
            {fmtDate(ev.date)}
          </div>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 800, fontStyle: 'italic', color: isCancelled ? 'rgba(220,100,100,0.7)' : '#e2e2e8', letterSpacing: '0.03em', textShadow: '0 2px 8px rgba(0,0,0,0.8)', lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: isCancelled ? 'line-through' : 'none' }}>
            {ev.name.toUpperCase()}
          </div>
        </div>
      </div>

      {/* Card body */}
      <div style={{ padding: '0.875rem 1rem', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Location + track info */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {ev.location && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ color: 'var(--outline)', fontSize: 11 }}>◈</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--on-surface-variant)', textTransform: 'uppercase' }}>{ev.location}</span>
            </div>
          )}
          {ev.track_length && (
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>↔</span> {Number(ev.track_length).toLocaleString('de-DE')} m
            </div>
          )}
        </div>
        {ev.start_address && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>📍</span> {ev.start_address}
            </div>
            <div style={{ display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
              <a href={`https://maps.apple.com/?q=${encodeURIComponent(ev.start_address)}`} target="_blank" rel="noopener noreferrer"
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--primary)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', padding: '2px 7px', textDecoration: 'none', display: 'inline-block' }}>
                ↗ APPLE MAPS
              </a>
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev.start_address)}`} target="_blank" rel="noopener noreferrer"
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--primary)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', padding: '2px 7px', textDecoration: 'none', display: 'inline-block' }}>
                ↗ GOOGLE MAPS
              </a>
            </div>
          </div>
        )}

        <div style={{ height: 1, background: 'rgba(59,73,75,0.3)' }} />

        {/* Race classes with start times */}
        {Array.isArray(ev.race_classes) && ev.race_classes.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {ev.race_classes.map((rc, i) => {
              const cName = typeof rc === 'string' ? rc : (rc.className || '');
              const cTime = typeof rc === 'object' ? rc.startTime : null;
              const cDate = typeof rc === 'object' ? rc.date : null;
              const showDate = cDate && ev.date_end;
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--on-surface-variant)', letterSpacing: '0.04em' }}>
                    {cName}
                  </span>
                  {(cTime || showDate) && (
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(var(--brand-secondary-rgb),0.8)', letterSpacing: '0.06em', flexShrink: 0, marginLeft: 8 }}>
                      {showDate && cDate ? new Date(cDate + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: 'short' }) + ' ' : ''}{cTime || ''}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Registered riders strip */}
        {(() => {
          const participating = regs.filter(r => r.status === 'participating' || r.status === 'assigned');
          const shown = participating.slice(0, 3);
          const extra = participating.length - shown.length;
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 36 }}>
              {participating.length === 0 ? (
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.08em', opacity: 0.5 }}>
                  NOCH KEINE ANMELDUNGEN
                </span>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {/* Avatar stack */}
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {shown.map((r, i) => {
                      const driver = allDrivers.find(d => d.id === r.driver_id);
                      if (!driver) return null;
                      const initials = (driver.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                      return (
                        <div key={r.id} style={{
                          width: 30, height: 30, borderRadius: '50%',
                          border: '2px solid rgba(17,19,24,0.9)',
                          marginLeft: i > 0 ? -8 : 0,
                          background: driver.photo_url ? 'transparent' : 'rgba(var(--brand-primary-rgb),0.15)',
                          overflow: 'hidden', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          zIndex: shown.length - i,
                          position: 'relative',
                        }}>
                          {driver.photo_url
                            ? <img src={driver.photo_url} alt={driver.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <span style={{ fontFamily: "var(--font-display)", fontSize: 9, fontWeight: 800, color: 'var(--brand-primary)' }}>{initials}</span>
                          }
                        </div>
                      );
                    })}
                    {extra > 0 && (
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%',
                        border: '2px solid rgba(17,19,24,0.9)',
                        marginLeft: -8, background: 'rgba(40,42,46,0.9)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, position: 'relative', zIndex: 0,
                      }}>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, color: 'var(--on-surface-variant)' }}>+{extra}</span>
                      </div>
                    )}
                  </div>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: 'var(--brand-primary)', letterSpacing: '0.04em' }}>
                    {participating.length} {participating.length === 1 ? 'Rider' : 'Riders'}
                  </span>
                </div>
              )}
            </div>
          );
        })()}

        {/* Spacer pushes action bar to bottom */}
        <div style={{ flex: 1 }} />

        {/* Action bar */}
        <div
          style={{ display: 'flex', gap: 6, paddingTop: 8, borderTop: '1px solid rgba(59,73,75,0.2)', marginTop: 4 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => navigate(`/events/${ev.id}`)}
            style={{ ...ghostBtn, flex: 1, color: 'var(--brand-primary)', borderColor: 'rgba(var(--brand-primary-rgb),0.3)', fontWeight: 700 }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--brand-primary-rgb),0.08)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            DETAILS →
          </button>
          {driverId && !isPast && (
            <button
              onClick={handleRegToggle}
              disabled={regPending}
              style={{
                ...ghostBtn, flexShrink: 0, fontWeight: 700, padding: '5px 14px', whiteSpace: 'nowrap',
                background: isIn ? 'rgba(var(--brand-primary-rgb),0.12)' : isOut ? 'rgba(255,180,171,0.08)' : 'transparent',
                color: isIn ? 'var(--brand-primary)' : isOut ? '#ffb4ab' : 'var(--on-surface-variant)',
                borderColor: isIn ? 'rgba(var(--brand-primary-rgb),0.35)' : isOut ? 'rgba(255,180,171,0.25)' : 'rgba(59,73,75,0.4)',
              }}
            >
              {regPending ? '…' : isIn ? '✓ DABEI' : isOut ? '✕ ABSAGE' : '+ ANMELDEN'}
            </button>
          )}
          <button
            onClick={() => onCalendar(ev)}
            title="Im Kalender öffnen"
            style={{ ...ghostBtn, flexShrink: 0 }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(var(--brand-primary-rgb),0.06)'; e.currentTarget.style.color = 'var(--primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--on-surface-variant)'; }}
          >
            📅
          </button>
          {isAdmin && (
            <>
              <button onClick={() => onEdit(ev)} style={{ ...ghostBtn, flexShrink: 0 }}>✎</button>
              <button onClick={() => onDelete(ev.id)} style={{ ...ghostBtn, flexShrink: 0, color: 'var(--error)', borderColor: 'rgba(255,180,171,0.2)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,180,171,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >✕</button>
            </>
          )}
        </div>
      </div>
      {showCalPrompt && (
        <CalendarPrompt
          ev={ev}
          driverCategory={driverCategory}
          onDismiss={() => setShowCalPrompt(false)}
        />
      )}
    </div>
  );
}

/* ─── List View Row ───────────────────────────────────────────── */
function EventListRow({ ev, isNext, isAdmin, registrations, allDrivers, driverId, driverCategory, token, onEdit, onDelete, onRefresh }) {
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);

  const regs = registrations[ev.id] || [];
  const ownReg = regs.find(r => r.driver_id === driverId);
  const isIn = ownReg?.status === 'participating' || ownReg?.status === 'assigned';
  const isOut = ownReg?.status === 'not_participating';

  const todayStart = new Date(); todayStart.setHours(0,0,0,0);
  const evDate = new Date(ev.date);
  const isPast = evDate < todayStart;
  const isCancelled = !!ev.is_cancelled;

  const participatingRegs = regs.filter(r => r.status === 'participating' || r.status === 'assigned');
  const shownRegs = participatingRegs.slice(0, 2);
  const extraCount = participatingRegs.length - shownRegs.length;

  const [showCalPrompt, setShowCalPrompt] = useState(false);

  const handleToggle = async (e) => {
    e.stopPropagation();
    if (!driverId || isPast) return;
    setPending(true);
    try {
      const newStatus = isIn ? 'not_participating' : 'participating';
      await axios.post(`/api/registrations`, { event_id: ev.id, driver_id: driverId, status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      await onRefresh();
      if (newStatus === 'participating') setShowCalPrompt(true);
    } catch (err) { console.error(err); }
    setPending(false);
  };

  return (
    <>
    {showCalPrompt && (
      <CalendarPrompt
        ev={ev}
        driverCategory={driverCategory}
        onDismiss={() => setShowCalPrompt(false)}
      />
    )}
    <div
      onClick={() => navigate(`/events/${ev.id}`)}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        background: isNext ? 'rgba(var(--brand-primary-rgb),0.04)' : 'transparent',
        border: `1px solid ${isNext ? 'rgba(var(--brand-primary-rgb),0.2)' : 'rgba(59,73,75,0.25)'}`,
        borderLeft: `3px solid ${isCancelled ? '#dc2626' : ev.is_mandatory ? '#ff9a1f' : isNext ? 'var(--brand-primary)' : 'rgba(59,73,75,0.4)'}`,
        cursor: 'pointer',
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--brand-primary-rgb),0.04)'}
      onMouseLeave={e => e.currentTarget.style.background = isNext ? 'rgba(var(--brand-primary-rgb),0.04)' : 'transparent'}
    >
      {/* Left: info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
        {/* Date block */}
        <div style={{ flexShrink: 0, textAlign: 'center', minWidth: 52 }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 1 }}>
            {new Date(ev.date).toLocaleDateString('de-DE', { weekday: 'short' })}
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, lineHeight: 1, color: isPast ? 'var(--outline)' : 'var(--on-surface)' }}>
            {new Date(ev.date).toLocaleDateString('de-DE', { day: '2-digit' })}
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--outline)', textTransform: 'uppercase' }}>
            {new Date(ev.date).toLocaleDateString('de-DE', { month: 'short' })} {new Date(ev.date).getFullYear()}
          </div>
        </div>

        {/* Divider */}
        <div style={{ width: 1, height: 32, background: 'rgba(59,73,75,0.35)', flexShrink: 0 }} />

        {/* Name + meta */}
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 800, fontStyle: 'italic', color: isCancelled ? 'rgba(220,100,100,0.7)' : isPast ? 'var(--on-surface-variant)' : 'var(--on-surface)', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: isCancelled ? 'line-through' : 'none' }}>
              {ev.name}
            </div>
            {isCancelled && (
              <span style={{ flexShrink: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', background: '#dc2626', color: '#fff', padding: '2px 6px' }}>ABGESAGT</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 3, flexWrap: 'wrap' }}>
            {ev.location && (
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.06em' }}>◈ {ev.location}</span>
            )}
            {ev.track_length && (
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.06em' }}>↔ {Number(ev.track_length).toLocaleString('de-DE')} m</span>
            )}
          </div>
          {ev.start_address && (
            <div style={{ display: 'flex', gap: 5, marginTop: 4 }} onClick={e => e.stopPropagation()}>
              <a href={`https://maps.apple.com/?q=${encodeURIComponent(ev.start_address)}`} target="_blank" rel="noopener noreferrer"
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--primary)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', padding: '2px 7px', textDecoration: 'none' }}>
                ↗ APPLE MAPS
              </a>
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev.start_address)}`} target="_blank" rel="noopener noreferrer"
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--primary)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', padding: '2px 7px', textDecoration: 'none' }}>
                ↗ GOOGLE MAPS
              </a>
            </div>
          )}
          {/* Avatar stack */}
          {participatingRegs.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 5 }}>
              {shownRegs.map((r, i) => {
                const driver = allDrivers.find(d => d.id === r.driver_id);
                if (!driver) return null;
                const initials = (driver.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                return (
                  <div key={r.id} style={{
                    width: 22, height: 22, borderRadius: '50%',
                    border: '2px solid rgba(17,19,24,0.9)',
                    marginLeft: i > 0 ? -6 : 0,
                    background: driver.photo_url ? 'transparent' : 'rgba(var(--brand-primary-rgb),0.15)',
                    overflow: 'hidden', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 2 - i, position: 'relative',
                  }}>
                    {driver.photo_url
                      ? <img src={driver.photo_url} alt={driver.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ fontFamily: "var(--font-display)", fontSize: 7, fontWeight: 800, color: 'var(--brand-primary)' }}>{initials}</span>
                    }
                  </div>
                );
              })}
              {extraCount > 0 && (
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  border: '2px solid rgba(17,19,24,0.9)',
                  marginLeft: -6, background: 'rgba(40,42,46,0.9)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, position: 'relative', zIndex: 0,
                }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, fontWeight: 700, color: 'var(--on-surface-variant)' }}>+{extraCount}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right: status + button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
        {isAdmin && (
          <>
            <button onClick={() => onEdit(ev)} style={{ ...ghostBtn, padding: '4px 8px' }}>✎</button>
            <button onClick={() => onDelete(ev.id)} style={{ ...ghostBtn, padding: '4px 8px', color: 'var(--error)', borderColor: 'rgba(255,180,171,0.2)' }}>✕</button>
          </>
        )}
        {driverId && !isPast && (
          <button
            onClick={handleToggle}
            disabled={pending}
            style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
              padding: '6px 12px', border: 'none', cursor: pending ? 'wait' : 'pointer',
              background: isIn ? 'rgba(var(--brand-primary-rgb),0.15)' : isOut ? 'rgba(255,180,171,0.1)' : 'rgba(59,73,75,0.25)',
              color: isIn ? 'var(--brand-primary)' : isOut ? '#ffb4ab' : 'var(--on-surface-variant)',
              transition: 'all 0.15s', whiteSpace: 'nowrap',
            }}
          >
            {pending ? '…' : isIn ? '✓ DABEI' : isOut ? '✕ ABSAGE' : '+ ANMELD.'}
          </button>
        )}
        {driverId && isPast && (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.06em' }}>
            {isIn ? '✓ WAR DABEI' : '—'}
          </span>
        )}
      </div>
    </div>
    </>
  );
}

/* ─── Calendar View ───────────────────────────────────────────── */
const DE_MONTHS = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
const DE_DAYS   = ['Mo','Di','Mi','Do','Fr','Sa','So'];

function CalendarView({ events, navigate }) {
  const today = new Date();
  const [calYear,  setCalYear]  = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth()); // 0-11

  const prevMonth = () => {
    if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); }
    else setCalMonth(m => m + 1);
  };
  const goToday = () => { setCalYear(today.getFullYear()); setCalMonth(today.getMonth()); };

  // Build event lookup: 'YYYY-MM-DD' → [event, ...]
  const eventsByDate = React.useMemo(() => {
    const map = {};
    events.forEach(ev => {
      if (!ev.date) return;
      const key = new Date(ev.date).toISOString().slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    return map;
  }, [events]);

  // Build calendar grid (always 6 rows × 7 cols, Mon-first)
  const cells = React.useMemo(() => {
    const firstDay = new Date(calYear, calMonth, 1);
    // getDay(): 0=Sun,1=Mon,…,6=Sat → convert to Mon-first: Mon=0…Sun=6
    const startOffset = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const total = 42; // 6 rows × 7 cols
    return Array.from({ length: total }, (_, i) => {
      const day = i - startOffset + 1;
      if (day < 1 || day > daysInMonth) return null;
      return day;
    });
  }, [calYear, calMonth]);

  const todayKey = today.toISOString().slice(0, 10);
  const isMobile = window.innerWidth < 768;

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {/* ── Month nav ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <button onClick={prevMonth} style={{ padding: '4px 10px', background: 'transparent', border: '1px solid var(--outline-variant)', color: 'var(--on-surface)', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 13, borderRadius: 2 }}>‹</button>
        <div style={{ flex: 1, textAlign: 'center', fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 700, color: 'var(--on-surface)', letterSpacing: '0.02em' }}>
          {DE_MONTHS[calMonth]} {calYear}
        </div>
        <button onClick={goToday} style={{ padding: '4px 8px', background: 'transparent', border: '1px solid var(--outline-variant)', color: 'var(--on-surface-variant)', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '0.08em', borderRadius: 2 }}>HEUTE</button>
        <button onClick={nextMonth} style={{ padding: '4px 10px', background: 'transparent', border: '1px solid var(--outline-variant)', color: 'var(--on-surface)', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 13, borderRadius: 2 }}>›</button>
      </div>

      {/* ── Day headers ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
        {DE_DAYS.map(d => (
          <div key={d} style={{ textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--on-surface-variant)', padding: '4px 0' }}>{d}</div>
        ))}
      </div>

      {/* ── Calendar grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, idx) => {
          if (day === null) return (
            <div key={idx} style={{ minHeight: isMobile ? 52 : 80, background: 'var(--surface-dim)', borderRadius: 2, opacity: 0.3 }} />
          );
          const dateKey = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const dayEvents = eventsByDate[dateKey] || [];
          const isToday = dateKey === todayKey;
          return (
            <div key={idx} style={{ minHeight: isMobile ? 52 : 80, background: isToday ? 'rgba(var(--brand-primary-rgb),0.08)' : 'var(--surface)', border: isToday ? '1px solid rgba(var(--brand-primary-rgb),0.4)' : '1px solid var(--outline-variant)', borderRadius: 2, padding: '4px 4px 3px', overflow: 'hidden', position: 'relative' }}>
              {/* Day number */}
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: isMobile ? 9 : 11, fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--brand-primary)' : 'var(--on-surface-variant)', marginBottom: 3, lineHeight: 1 }}>{day}</div>
              {/* Events */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {dayEvents.slice(0, isMobile ? 1 : 3).map(ev => (
                  <button
                    key={ev.id}
                    onClick={() => navigate(`/events/${ev.id}`)}
                    title={ev.name}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: isMobile ? '1px 3px' : '2px 5px', background: 'rgba(var(--brand-primary-rgb),0.18)', border: 'none', borderLeft: '2px solid var(--brand-primary)', color: 'var(--brand-primary)', fontFamily: "'JetBrains Mono', monospace", fontSize: isMobile ? 7 : 9, fontWeight: 600, cursor: 'pointer', borderRadius: '0 2px 2px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '0.02em' }}
                  >
                    {ev.name}
                  </button>
                ))}
                {dayEvents.length > (isMobile ? 1 : 3) && (
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, color: 'var(--on-surface-variant)', paddingLeft: 3 }}>+{dayEvents.length - (isMobile ? 1 : 3)}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Main Events component ───────────────────────────────────── */
function Events({ token, role, isCoAdmin = false, driverId, ownerDriverId, isRacing, driverCategory }) {
  const isAdmin = role === 'admin';
  const isAdminOrCoAdmin = isAdmin || isCoAdmin;
  const effectiveDriverId = isAdmin ? (isRacing ? ownerDriverId : null) : driverId;
  const navigate = useNavigate();
  const { regsByEvent, refresh: refreshRegs } = useRegistrations();
  const t = useT();

  const [events, setEvents] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('eventsViewMode') || 'grid');
  const [mandatoryOnly, setMandatoryOnly] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formCats, setFormCats] = useState(EMPTY_CATS);
  const [message, setMessage] = useState('');

  // rad-net import state
  const [showImport, setShowImport] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importEvents, setImportEvents] = useState([]);
  const [importSelected, setImportSelected] = useState(new Set());
  const [importLv, setImportLv] = useState(new Set());
  const [importDisziplin, setImportDisziplin] = useState('');
  const [importKlassen, setImportKlassen] = useState(new Set());
  const [importSearch, setImportSearch] = useState('');
  const [importMsg, setImportMsg] = useState('');
  const [importing, setImporting] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [evRes, drRes] = await Promise.all([
        axios.get('/api/events', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/drivers', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setEvents(evRes.data);
      setAllDrivers(drRes.data);
      await refreshRegs();
      setLoading(false);
    } catch (err) { console.error(err); setLoading(false); }
  };

  const showMsg = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };

  const catsToArray = () =>
    CATEGORIES.filter((c) => formCats[c]?.enabled).map((c) => ({ category: c, start_time: formCats[c].start_time || null }));

  const arrayToCats = (arr = []) => {
    const result = { ...EMPTY_CATS };
    arr.forEach(({ category, start_time }) => {
      if (result[category] !== undefined) result[category] = { enabled: true, start_time: start_time || '' };
    });
    return result;
  };

  const handleCatChange = (cat, field, value) =>
    setFormCats((prev) => ({ ...prev, [cat]: { ...prev[cat], [field]: value } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, categories: catsToArray() };
      if (editingEvent) {
        await axios.put(`/api/events/${editingEvent.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        showMsg(t('events_msg_updated'));
      } else {
        await axios.post('/api/events', payload, { headers: { Authorization: `Bearer ${token}` } });
        showMsg(t('events_msg_created'));
      }
      closeForm(); loadData();
    } catch (err) { showMsg('Fehler: ' + (err.response?.data?.error || err.message)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('events_confirm_delete'))) return;
    try {
      await axios.delete(`/api/events/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      showMsg(t('events_msg_deleted')); loadData();
    } catch { showMsg(t('events_msg_delete_error')); }
  };

  const openCreate = () => { setEditingEvent(null); setFormData(EMPTY_FORM); setFormCats(EMPTY_CATS); setShowForm(true); };

  const openEdit = (ev) => {
    setEditingEvent(ev);
    const dateVal = ev.date ? (ev.date.includes('T') ? ev.date.split('T')[0] : ev.date) : '';
    setFormData({
      name: ev.name || '', date: dateVal, location: ev.location || '', start_address: ev.start_address || '',
      track_type: ev.track_type || '', track_length: ev.track_length != null ? String(ev.track_length) : '',
      description: ev.description || '', url: ev.url || '', is_mandatory: !!ev.is_mandatory, is_cancelled: !!ev.is_cancelled,
    });
    setFormCats(arrayToCats(ev.categories || []));
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditingEvent(null); };

  // ── rad-net import handlers ────────────────────────────────────────────────
  const LV_OPTIONS = [
    { code: 'BAD', label: 'Baden' }, { code: 'BAY', label: 'Bayern' },
    { code: 'BER', label: 'Berlin' }, { code: 'BRA', label: 'Brandenburg' },
    { code: 'BRE', label: 'Bremen' }, { code: 'HAM', label: 'Hamburg' },
    { code: 'HES', label: 'Hessen' }, { code: 'MEV', label: 'Mecklenburg-Vorpommern' },
    { code: 'NIE', label: 'Niedersachsen' }, { code: 'NRW', label: 'Nordrhein-Westfalen' },
    { code: 'RLP', label: 'Rheinland-Pfalz' }, { code: 'SAA', label: 'Saarland' },
    { code: 'SAC', label: 'Sachsen' }, { code: 'SAN', label: 'Sachsen-Anhalt' },
    { code: 'SHO', label: 'Schleswig-Holstein' }, { code: 'THÜ', label: 'Thüringen' },
    { code: 'WTB', label: 'Württemberg' },
  ];
  const DISZ_OPTIONS = [
    { code: 'Str.', label: 'Straße' }, { code: 'MTB', label: 'MTB' },
    { code: 'Bahn', label: 'Bahn' }, { code: 'Gravel', label: 'Gravel' },
    { code: 'CX', label: 'Cyclo-Cross' }, { code: 'KR', label: 'Kunstrad' },
  ];

  const openImport = async () => {
    // Auto-derive relevant Rennklassen from the team's driver categories
    const teamKlassen = getTeamKlassenIds(allDrivers);
    setImportKlassen(teamKlassen);
    setShowImport(true);
    setImportSelected(new Set());
    setImportMsg('');
    setImportEvents([]);
    // Auto-load basic listing; SUCHEN with Rennklassen loads enriched data
    await fetchImportEvents(importLv, importDisziplin, false);
  };

  // useEnriched=true → fetches detail pages for every event (slower, includes raceClasses)
  // lvSet can be a Set of codes or a single string (for backward compat)
  const fetchImportEvents = async (lvSet, disziplin, useEnriched = false) => {
    setImportLoading(true);
    setImportMsg('');
    try {
      const params = new URLSearchParams();
      const lvStr = lvSet instanceof Set ? [...lvSet].join(',') : (lvSet || '');
      if (lvStr) params.set('lv', lvStr);
      if (disziplin) params.set('disziplin', disziplin);
      const endpoint = useEnriched
        ? `/api/admin/radnet-events/with-classes?${params}`
        : `/api/admin/radnet-events?${params}`;
      const res = await axios.get(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      setImportEvents(res.data);
      setImportSelected(new Set());
    } catch (err) {
      setImportMsg('Fehler beim Laden: ' + (err.response?.data?.error || err.message));
    }
    setImportLoading(false);
  };

  // Explicit search trigger — uses enriched endpoint when Rennklassen + LV are set
  const handleSearch = () => {
    const useEnriched = importKlassen.size > 0 && importLv.size > 0;
    fetchImportEvents(importLv, importDisziplin, useEnriched);
  };

  const toggleLv = (code) => {
    setImportLv(prev => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      fetchImportEvents(next, importDisziplin, false);
      return next;
    });
  };

  const toggleKlasse = (id) => {
    setImportKlassen(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleImportSelect = (id) => {
    setImportSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const filtered = getFilteredImportEvents(importEvents, importKlassen);
    const allSelected = filtered.length > 0 && filtered.every(ev => importSelected.has(ev._origIdx));
    setImportSelected(prev => {
      const next = new Set(prev);
      if (allSelected) {
        filtered.forEach(ev => next.delete(ev._origIdx));
      } else {
        filtered.forEach(ev => next.add(ev._origIdx));
      }
      return next;
    });
  };

  const handleImport = async () => {
    const filtered = getFilteredImportEvents(importEvents, importKlassen);
    const toImport = filtered.filter(ev => importSelected.has(ev._origIdx));
    if (!toImport.length) return;
    setImporting(true);

    // If events are not enriched yet (no raceClasses field), fetch detail pages first
    const needsDetail = toImport.filter(ev => ev.radnetId && ev.raceClasses === undefined);
    const details = {};
    if (needsDetail.length > 0) {
      setImportMsg('Detail-Ausschreibungen werden geladen…');
      const batches = [];
      for (let i = 0; i < needsDetail.length; i += 3) batches.push(needsDetail.slice(i, i + 3));
      for (const batch of batches) {
        await Promise.all(batch.map(async (ev) => {
          try {
            const res = await axios.get(
              `/api/admin/radnet-events/detail?eventId=${ev.radnetId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            details[ev.radnetId] = res.data;
          } catch { /* import with listing data as fallback */ }
        }));
      }
    }

    // ── Create events ─────────────────────────────────────────────────────────
    setImportMsg('Events werden importiert…');
    let ok = 0;
    const selectedKlassenIds = [...importKlassen];
    for (const ev of toImport) {
      try {
        // Prefer enriched fields (from /with-classes), fall back to detail-fetched, then listing
        const d = ev.raceClasses !== undefined ? {} : (ev.radnetId ? (details[ev.radnetId] || {}) : {});
        const location      = ev.raceClasses !== undefined ? ev.location      : (d.location      || ev.location || '');
        const start_address = ev.raceClasses !== undefined ? ev.start_address : (d.start_address || ev.location || '');
        const track_type    = ev.raceClasses !== undefined ? ev.track_type    : (d.track_type    || '');
        const baseDesc      = ev.raceClasses !== undefined ? ev.description   : (d.description   || '');
        const dateRangeNote = ev.dateEnd ? `Rennen bis ${new Date(ev.dateEnd).toLocaleDateString('de-DE')}` : '';
        const description   = baseDesc || dateRangeNote;

        // Race classes: all from enriched event or detail fetch, filtered to team-relevant ones
        const allRaceClasses = ev.raceClasses !== undefined
          ? (ev.raceClasses || [])
          : (ev.radnetId ? (details[ev.radnetId]?.raceClasses || []) : []);
        const race_classes = selectedKlassenIds.length === 0
          ? allRaceClasses
          : allRaceClasses.filter(rc => rcMatchesKlassen(rc, selectedKlassenIds));

        // Build event schedule (categories) from race_classes.
        // Same class on different days → add weekday suffix to keep names unique.
        const catEntries = new Map(); // className → [{ time, date }]
        for (const rc of race_classes) {
          const name = typeof rc === 'string' ? rc : (rc.className || '');
          if (!name) continue;
          if (!catEntries.has(name)) catEntries.set(name, []);
          catEntries.get(name).push({
            time: typeof rc === 'object' ? (rc.startTime || null) : null,
            date: typeof rc === 'object' ? (rc.date || null) : null,
          });
        }
        const categories = [];
        for (const [name, entries] of catEntries) {
          if (entries.length === 1) {
            categories.push({ category: name, start_time: entries[0].time });
          } else {
            // Multi-day: disambiguate with weekday abbreviation
            for (const { time, date } of entries) {
              const day = date
                ? new Date(date + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'short' })
                : '';
              categories.push({ category: `${name}${day ? ` (${day})` : ''}`, start_time: time });
            }
          }
        }

        await axios.post('/api/events', {
          name:          ev.name,
          date:          ev.date,
          location,
          start_address,
          url:           ev.url || '',
          description,
          track_type,
          track_length:  '',
          is_mandatory:  false,
          categories,
          race_classes,
        }, { headers: { Authorization: `Bearer ${token}` } });
        ok++;
      } catch { /* skip duplicates / errors */ }
    }

    setImporting(false);
    setImportMsg(`${ok} von ${toImport.length} Events importiert.`);
    setImportSelected(new Set());
    await loadData();
  };

  // Derived: importEvents filtered by Rennklassen + free-text search (client-side)
  const filteredImportEvents = (() => {
    let evs = getFilteredImportEvents(importEvents, importKlassen);
    if (importSearch.trim()) {
      const q = importSearch.trim().toLowerCase();
      evs = evs.filter(ev =>
        (ev.name     || '').toLowerCase().includes(q) ||
        (ev.location || '').toLowerCase().includes(q)
      );
    }
    return evs;
  })();

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const upcomingEvents = events.filter((e) => new Date(e.date) >= today).sort((a, b) => new Date(a.date) - new Date(b.date));
  const pastEvents    = events.filter((e) => new Date(e.date) < today).sort((a, b) => new Date(b.date) - new Date(a.date));
  const baseEvents    = activeTab === 'upcoming' ? upcomingEvents : pastEvents;
  const currentEvents = mandatoryOnly ? baseEvents.filter((e) => e.is_mandatory) : baseEvents;
  const nextEvent     = upcomingEvents[0] || null;

  if (loading) return <div style={{ color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, letterSpacing: '0.1em' }}>{t('events_loading').toUpperCase()}</div>;

  return (
    <div>
      <style>{`
        @media (max-width: 767px) {
          .ev-mobile  { display: block !important; }
          .ev-desktop { display: none  !important; }
          .ev-mobile-header { display: flex !important; }
          .ev-mobile-tabs   { display: flex !important; }
          .ev-mobile-fab    { display: flex !important; }
          .ev-grid { display: flex !important; flex-direction: column !important; gap: 12px !important; }
        }
        @media (min-width: 768px) {
          .ev-mobile  { display: none  !important; }
          .ev-desktop { display: block !important; }
          .ev-mobile-header { display: none !important; }
          .ev-mobile-tabs   { display: none !important; }
          .ev-mobile-fab    { display: none !important; }
          .ev-grid {
            display: grid !important;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)) !important;
            grid-auto-rows: 1fr !important;
            gap: 1.25rem !important;
          }
        }
        .ev-featured-card {
          background: rgba(30,32,36,0.5);
          border: 1px solid rgba(var(--brand-primary-rgb),0.3);
          box-shadow: inset 0 0 10px rgba(var(--brand-primary-rgb),0.07);
          position: relative; overflow: hidden; padding: 16px;
        }
        .ev-featured-card::before {
          content: ''; position: absolute; top: 0; left: 0; bottom: 0;
          width: 3px; background: var(--brand-primary); box-shadow: 0 0 8px rgba(var(--brand-primary-rgb),0.6);
        }
        .ev-regular-card {
          background: rgba(30,32,36,0.2);
          border: 1px solid rgba(59,73,75,0.4);
          padding: 16px; transition: border-color 0.2s; position: relative; overflow: hidden;
        }
        .ev-tab-btn {
          flex: 1; padding: 8px 0;
          font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 700;
          letter-spacing: 0.1em; border: none; cursor: pointer; border-radius: 6px; transition: all 0.15s;
        }
        .ev-tab-btn.active   { background: var(--brand-primary); color: #002022; box-shadow: 0 2px 8px rgba(var(--brand-primary-rgb),0.3); }
        .ev-tab-btn.inactive { background: transparent; color: #b9cacb; }
        @keyframes ev-pulse-dot { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.8);opacity:0.4} }
        .ev-live-dot { animation: ev-pulse-dot 2s infinite; }
      `}</style>

      {/* ─── MOBILE HEADER ─────────────────────────────────── */}
      <div className="ev-mobile-header" style={{ display: 'none', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 32, fontWeight: 700, fontStyle: 'italic', textTransform: 'uppercase', color: 'var(--brand-primary)', lineHeight: 1.1 }}>{t('events_title_mobile')}</h2>
          {isAdminOrCoAdmin && (
            <button onClick={openImport} style={{ flexShrink: 0, padding: '7px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer', border: '1px solid rgba(var(--brand-secondary-rgb),0.4)', background: 'rgba(var(--brand-secondary-rgb),0.07)', color: 'var(--brand-secondary)' }}>
              ⬇ IMPORT
            </button>
          )}
        </div>
      </div>

      {/* ─── DESKTOP HEADER ────────────────────────────────── */}
      <div className="ev-desktop" style={{ display: 'none', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16 }}>
          <h1 style={{ margin: 0 }}>{t('events_title')}</h1>
          {isAdminOrCoAdmin && (
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={openImport} style={{ flexShrink: 0, padding: '10px 16px', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer', border: '1px solid rgba(var(--brand-secondary-rgb),0.4)', background: 'rgba(var(--brand-secondary-rgb),0.07)', color: 'var(--brand-secondary)', display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--brand-secondary-rgb),0.14)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(var(--brand-secondary-rgb),0.07)'}>
                ⬇ EVENT IMPORT
              </button>
              <button className="btn btn-primary" onClick={openCreate} style={{ flexShrink: 0, padding: '10px 20px', boxShadow: '0 0 15px rgba(var(--brand-primary-rgb),0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
                ＋ {t('events_add').toUpperCase()}
              </button>
            </div>
          )}
        </div>
      </div>

      {message && <div className={`alert ${message.includes('Fehler') ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: '1rem' }}>{message}</div>}

      {/* ─── MOBILE TABS ───────────────────────────────────── */}
      <div className="ev-mobile-tabs" style={{ display: 'none', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, padding: 4, background: 'rgba(26,28,32,0.8)', border: '1px solid rgba(59,73,75,0.3)', borderRadius: 8, width: '100%' }}>
          <button className={`ev-tab-btn ${activeTab === 'upcoming' ? 'active' : 'inactive'}`} onClick={() => setActiveTab('upcoming')}>{t('events_tab_upcoming').toUpperCase()}</button>
          <button className={`ev-tab-btn ${activeTab === 'past' ? 'active' : 'inactive'}`} onClick={() => setActiveTab('past')}>{t('events_tab_past').toUpperCase()}</button>
        </div>
      </div>
      <div className="ev-mobile-tabs" style={{ display: 'none', marginBottom: 16, justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={() => setMandatoryOnly((v) => !v)} style={{
          padding: '7px 14px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', borderRadius: 6, border: mandatoryOnly ? '1px solid var(--primary)' : '1px solid rgba(59,73,75,0.4)', background: mandatoryOnly ? 'rgba(var(--brand-primary-rgb),0.12)' : 'transparent', color: mandatoryOnly ? 'var(--primary)' : 'var(--on-surface-variant)', transition: 'all 0.15s',
        }}>
          {mandatoryOnly ? `● ${t('events_mandatory').toUpperCase()}` : `○ ${t('events_mandatory').toUpperCase()}`}
        </button>
        {/* Mobile view toggle */}
        <div style={{ display: 'flex', border: '1px solid rgba(59,73,75,0.4)', overflow: 'hidden', borderRadius: 6 }}>
          {[{ key: 'grid', icon: '⊞' }, { key: 'list', icon: '☰' }, { key: 'calendar', icon: '▦' }].map(({ key, icon }, i) => (
            <button key={key} onClick={() => { setViewMode(key); localStorage.setItem('eventsViewMode', key); }}
              style={{ padding: '6px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 14, background: viewMode === key ? 'rgba(var(--brand-primary-rgb),0.15)' : 'transparent', color: viewMode === key ? 'var(--brand-primary)' : 'var(--outline)', border: 'none', borderLeft: i > 0 ? '1px solid rgba(59,73,75,0.4)' : 'none', cursor: 'pointer', transition: 'all 0.15s' }}
            >
              {icon}
            </button>
          ))}
        </div>
      </div>

      {/* ─── DESKTOP TABS ──────────────────────────────────── */}
      <div className="ev-desktop" style={{ display: 'none', marginBottom: '1.5rem', borderBottom: '1px solid rgba(59,73,75,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {[{ key: 'upcoming', label: t('events_tab_upcoming').toUpperCase() }, { key: 'past', label: t('events_tab_past').toUpperCase() }].map(({ key, label }) => (
              <button key={key} onClick={() => setActiveTab(key)} style={{ padding: '10px 16px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', background: 'transparent', border: 'none', color: activeTab === key ? 'var(--primary)' : 'var(--on-surface-variant)', borderBottom: activeTab === key ? '2px solid var(--primary)' : '2px solid transparent', cursor: 'pointer', marginBottom: -1 }}>
                {label}
              </button>
            ))}
            <div style={{ width: 1, height: 16, background: 'rgba(59,73,75,0.4)', margin: '0 4px' }} />
            <button onClick={() => setMandatoryOnly((v) => !v)} style={{
              padding: '5px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', border: mandatoryOnly ? '1px solid var(--primary)' : '1px solid rgba(59,73,75,0.4)', background: mandatoryOnly ? 'rgba(var(--brand-primary-rgb),0.12)' : 'transparent', color: mandatoryOnly ? 'var(--primary)' : 'var(--on-surface-variant)', transition: 'all 0.15s',
            }}>
              {mandatoryOnly ? `● ${t('events_mandatory').toUpperCase()}` : `○ ${t('events_mandatory').toUpperCase()}`}
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--outline)', letterSpacing: '0.08em' }}>
              {currentEvents.length} {t('events_count_races').toUpperCase()}
            </span>
            {/* View toggle */}
            <div style={{ display: 'flex', border: '1px solid rgba(59,73,75,0.4)', overflow: 'hidden' }}>
              {[{ key: 'grid', icon: '⊞' }, { key: 'list', icon: '☰' }, { key: 'calendar', icon: '▦' }].map(({ key, icon }, i) => (
                <button key={key} onClick={() => { setViewMode(key); localStorage.setItem('eventsViewMode', key); }}
                  style={{ padding: '4px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 13, background: viewMode === key ? 'rgba(var(--brand-primary-rgb),0.15)' : 'transparent', color: viewMode === key ? 'var(--brand-primary)' : 'var(--outline)', border: 'none', borderLeft: i > 0 ? '1px solid rgba(59,73,75,0.4)' : 'none', cursor: 'pointer', transition: 'all 0.15s' }}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ─── EVENT LIST / GRID ─────────────────────────────── */}
      {/* ── Calendar view (full-width, ignores active tab filter) ── */}
      {viewMode === 'calendar' && (
        <CalendarView events={events} navigate={navigate} />
      )}

      {currentEvents.length === 0 && viewMode !== 'calendar' ? (
        <div style={{ color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, letterSpacing: '0.1em', padding: '2rem 0' }}>
          {activeTab === 'upcoming' ? t('events_empty_upcoming').toUpperCase() : t('events_empty_past').toUpperCase()}
        </div>
      ) : viewMode !== 'calendar' ? (
        <>
          {/* Mobile — grid or list */}
          <div className="ev-mobile">
            {viewMode === 'grid' ? (
              <div className="ev-grid" style={{ marginBottom: '1.5rem' }}>
                {currentEvents.map((ev, idx) => (
                  <EventCard
                    key={ev.id} ev={ev} idx={idx}
                    isNext={ev.id === nextEvent?.id && activeTab === 'upcoming'}
                    isAdmin={isAdminOrCoAdmin} registrations={regsByEvent} allDrivers={allDrivers}
                    onEdit={openEdit} onDelete={handleDelete} onCalendar={openInAppleCalendar}
                    driverId={effectiveDriverId} driverCategory={driverCategory} token={token} onRefresh={refreshRegs}
                  />
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: '1.5rem' }}>
                {currentEvents.map((ev) => (
                  <EventListRow
                    key={ev.id} ev={ev}
                    isNext={ev.id === nextEvent?.id && activeTab === 'upcoming'}
                    isAdmin={isAdminOrCoAdmin} registrations={regsByEvent} allDrivers={allDrivers}
                    driverId={effectiveDriverId} driverCategory={driverCategory} token={token}
                    onEdit={openEdit} onDelete={handleDelete} onRefresh={refreshRegs}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Desktop — grid or list depending on viewMode */}
          <div className="ev-desktop" style={{ display: 'none' }}>
            {viewMode === 'grid' ? (
              <div className="ev-grid" style={{ marginBottom: '2rem' }}>
                {currentEvents.map((ev, idx) => (
                  <EventCard
                    key={ev.id} ev={ev} idx={idx}
                    isNext={ev.id === nextEvent?.id && activeTab === 'upcoming'}
                    isAdmin={isAdminOrCoAdmin} registrations={regsByEvent} allDrivers={allDrivers}
                    onEdit={openEdit} onDelete={handleDelete} onCalendar={openInAppleCalendar}
                    driverId={effectiveDriverId} driverCategory={driverCategory} token={token} onRefresh={refreshRegs}
                  />
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: '2rem' }}>
                {currentEvents.map((ev) => (
                  <EventListRow
                    key={ev.id} ev={ev}
                    isNext={ev.id === nextEvent?.id && activeTab === 'upcoming'}
                    isAdmin={isAdminOrCoAdmin} registrations={regsByEvent} allDrivers={allDrivers}
                    driverId={effectiveDriverId} driverCategory={driverCategory} token={token}
                    onEdit={openEdit} onDelete={handleDelete} onRefresh={refreshRegs}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}

      {/* ─── MOBILE FAB (Admin) ────────────────────────────── */}
      {isAdminOrCoAdmin && (
        <button
          className="ev-mobile-fab"
          onClick={openCreate}
          style={{ display: 'none', position: 'fixed', bottom: 112, right: 16, width: 56, height: 56, background: 'var(--brand-primary)', color: '#002022', border: 'none', borderRadius: '50%', cursor: 'pointer', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 300, boxShadow: '0 0 20px rgba(var(--brand-primary-rgb),0.4)', zIndex: 40 }}
        >
          +
        </button>
      )}

      {/* ─── CREATE/EDIT MODAL ─────────────────────────────── */}
      {showForm && (
        <div onClick={(e) => e.target === e.currentTarget && closeForm()} style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: '16px' }}>
          <div style={{ width: '100%', maxWidth: 600, background: 'var(--surface)', border: '1px solid rgba(59,73,75,0.4)', boxShadow: 'inset 0 0 20px rgba(var(--brand-primary-rgb),0.06), 0 24px 60px rgba(0,0,0,0.6)', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid rgba(59,73,75,0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(26,28,32,0.5)', flexShrink: 0 }}>
              <div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', marginBottom: 4 }}>{editingEvent ? 'EDIT_EVENT' : 'NEW_EVENT'}</div>
                <h2 style={{ margin: 0, fontSize: 18, fontStyle: 'italic' }}>{editingEvent ? editingEvent.name.toUpperCase() : t('events_form_new').toUpperCase()}</h2>
              </div>
              <button onClick={closeForm} style={{ background: 'none', border: 'none', color: 'var(--on-surface-variant)', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: 0, overflowY: 'auto', flex: 1 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', textTransform: 'uppercase', marginBottom: 12 }}>{t('events_form_section_general').toUpperCase()}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.5rem' }}>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>{t('events_form_name').toUpperCase()} *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>{t('events_form_date').toUpperCase()} *</label>
                  <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>{t('events_form_location').toUpperCase()}</label>
                  <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="z.B. Berlin, DE" />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>START / ZIEL ADRESSE</label>
                  <input type="text" value={formData.start_address} onChange={(e) => setFormData({ ...formData, start_address: e.target.value })} placeholder="z.B. Hauptstraße 1, 12345 Berlin" />
                </div>
                <div className="form-group">
                  <label>{t('events_form_track_type').toUpperCase()}</label>
                  <select value={formData.track_type} onChange={(e) => setFormData({ ...formData, track_type: e.target.value })}>
                    <option value="">— {t('events_form_select')} —</option>
                    <option>Rundstrecke / Crit</option>
                    <option>Straße / Road Race</option>
                    <option>Gelände / MTB</option>
                    <option>Zeitfahren / TT</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('events_form_track_length').toUpperCase()}</label>
                  <input type="number" value={formData.track_length} onChange={(e) => setFormData({ ...formData, track_length: e.target.value })} placeholder="z.B. 1200" min="0" />
                </div>
                <div className="form-group">
                  <label>{t('events_form_url').toUpperCase()}</label>
                  <input type="url" value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} placeholder="https://..." />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>{t('events_form_desc').toUpperCase()}</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="2" placeholder={t('events_form_desc_placeholder')} />
                </div>

                {/* Pflichttermin toggle */}
                <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', border: `1px solid ${formData.is_mandatory ? 'rgba(255,160,50,0.45)' : 'rgba(59,73,75,0.3)'}`, background: formData.is_mandatory ? 'rgba(255,140,0,0.06)' : 'rgba(26,28,32,0.4)', transition: 'all 0.15s', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setFormData({ ...formData, is_mandatory: !formData.is_mandatory })}>
                  <div style={{ width: 40, height: 22, borderRadius: 11, background: formData.is_mandatory ? '#ff9a1f' : 'rgba(59,73,75,0.5)', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
                    <div style={{ position: 'absolute', top: 3, left: formData.is_mandatory ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' }} />
                  </div>
                  <div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: formData.is_mandatory ? '#ff9a1f' : 'var(--on-surface-variant)' }}>{t('events_mandatory').toUpperCase()}</div>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 12, color: 'var(--outline)', marginTop: 2 }}>{t('events_mandatory_desc')}</div>
                  </div>
                </div>

                {/* Abgesagt toggle */}
                <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', border: `1px solid ${formData.is_cancelled ? 'rgba(220,50,50,0.45)' : 'rgba(59,73,75,0.3)'}`, background: formData.is_cancelled ? 'rgba(180,20,20,0.06)' : 'rgba(26,28,32,0.4)', transition: 'all 0.15s', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setFormData({ ...formData, is_cancelled: !formData.is_cancelled })}>
                  <div style={{ width: 40, height: 22, borderRadius: 11, background: formData.is_cancelled ? '#dc2626' : 'rgba(59,73,75,0.5)', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
                    <div style={{ position: 'absolute', top: 3, left: formData.is_cancelled ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' }} />
                  </div>
                  <div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: formData.is_cancelled ? '#dc2626' : 'var(--on-surface-variant)' }}>ABGESAGT</div>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 12, color: 'var(--outline)', marginTop: 2 }}>Event wurde abgesagt oder findet nicht statt</div>
                  </div>
                </div>
              </div>

              <div style={{ height: 1, background: 'linear-gradient(90deg, rgba(59,73,75,0.4) 0%, transparent 100%)', margin: '1rem 0' }} />
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', textTransform: 'uppercase', marginBottom: 12 }}>
                {t('events_form_section_cats').toUpperCase()}
              </div>
              <CategoryEditor cats={formCats} onChange={handleCatChange} />

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: '1.25rem', marginTop: '0.5rem', borderTop: '1px solid rgba(59,73,75,0.2)' }}>
                <button type="button" className="btn btn-secondary" onClick={closeForm}>{t('general_cancel').toUpperCase()}</button>
                <button type="submit" className="btn btn-primary" style={{ boxShadow: '0 0 12px rgba(var(--brand-primary-rgb),0.3)' }}>
                  {editingEvent ? t('events_form_update').toUpperCase() : t('events_form_create').toUpperCase()}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── RAD-NET IMPORT MODAL ──────────────────────────── */}
      {showImport && (
        <div onClick={(e) => e.target === e.currentTarget && setShowImport(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: '16px' }}>
          <div style={{ width: '100%', maxWidth: 680, background: 'var(--surface)', border: '1px solid rgba(var(--brand-secondary-rgb),0.25)', boxShadow: 'inset 0 0 20px rgba(var(--brand-secondary-rgb),0.04), 0 24px 60px rgba(0,0,0,0.7)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid rgba(59,73,75,0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(26,28,32,0.5)', flexShrink: 0 }}>
              <div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--brand-secondary)', marginBottom: 2 }}>EVENT IMPORT</div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.08em' }}>
                  {importLoading
                    ? (importKlassen.size > 0 ? 'LADE AUSSCHREIBUNGEN (~15 SEK.)...' : 'LADE AUSSCHREIBUNGEN...')
                    : importEvents.length === 0
                      ? 'FILTER WÄHLEN UND SUCHEN'
                      : (importKlassen.size > 0 || importSearch.trim())
                        ? `${filteredImportEvents.length} VON ${importEvents.length} EVENTS`
                        : `${importEvents.length} EVENTS GEFUNDEN`}
                </div>
              </div>
              <button onClick={() => setShowImport(false)} style={{ background: 'transparent', border: 'none', color: 'var(--outline)', fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 4 }}>✕</button>
            </div>

            {/* Rennklassen chips */}
            <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid rgba(59,73,75,0.15)', background: 'rgba(15,17,20,0.3)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', textTransform: 'uppercase' }}>
                  Rennklassen · {importKlassen.size} aktiv
                </span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, color: 'rgba(var(--brand-secondary-rgb),0.6)', letterSpacing: '0.06em' }}>
                  ✦ AUTO AUS FAHRERPROFILEN
                </span>
              </div>
              {[{ key: 'm', label: '♂' }, { key: 'f', label: '♀' }, { key: null, label: '—' }].map(({ key, label }) => {
                const opts = RENNKLASSEN_OPTIONS.filter(o => o.gender === key);
                if (!opts.length) return null;
                return (
                  <div key={String(key)} style={{ marginBottom: 5 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(59,73,75,0.6)', marginBottom: 4 }}>{label}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {opts.map(opt => {
                        const active = importKlassen.has(opt.id);
                        return (
                          <button key={opt.id} onClick={() => toggleKlasse(opt.id)}
                            style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.07em', padding: '4px 9px', border: `1px solid ${active ? 'rgba(var(--brand-secondary-rgb),0.6)' : 'rgba(59,73,75,0.4)'}`, background: active ? 'rgba(var(--brand-secondary-rgb),0.12)' : 'transparent', color: active ? 'var(--brand-secondary)' : 'var(--on-surface-variant)', cursor: 'pointer', transition: 'all 0.1s' }}>
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {importKlassen.size > 0 && importLv.size === 0 && (
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(255,180,0,0.7)', marginTop: 6, letterSpacing: '0.06em' }}>
                  ⚠ Wähle mindestens einen Landesverband für die Rennklassen-Filterung
                </div>
              )}
            </div>

            {/* LV / Disziplin filter + Suchen */}
            <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid rgba(59,73,75,0.2)', background: 'rgba(15,17,20,0.4)', display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>
              {/* Landesverband multi-select chips */}
              <div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 6 }}>
                  Landesverband {importLv.size > 0 && <span style={{ color: 'var(--brand-primary)' }}>({importLv.size} gewählt)</span>}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {LV_OPTIONS.map(opt => {
                    const active = importLv.has(opt.code);
                    return (
                      <button key={opt.code} onClick={() => toggleLv(opt.code)}
                        style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', padding: '3px 8px', border: `1px solid ${active ? 'rgba(var(--brand-primary-rgb),0.6)' : 'rgba(59,73,75,0.4)'}`, background: active ? 'rgba(var(--brand-primary-rgb),0.12)' : 'transparent', color: active ? 'var(--brand-primary)' : 'var(--on-surface-variant)', cursor: 'pointer', transition: 'all 0.15s' }}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Disziplin + Suchen row */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 120 }}>
                  <label style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', textTransform: 'uppercase' }}>Disziplin</label>
                  <select value={importDisziplin} onChange={e => { setImportDisziplin(e.target.value); fetchImportEvents(importLv, e.target.value, false); }}
                    style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: 'var(--surface)', border: '1px solid rgba(59,73,75,0.4)', color: 'var(--on-surface)', padding: '6px 8px', outline: 'none' }}>
                    <option value=''>Alle</option>
                    {DISZ_OPTIONS.map(d => <option key={d.code} value={d.code}>{d.label}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 160 }}>
                  <label style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', textTransform: 'uppercase' }}>Suche (Name / Ort)</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={importSearch}
                      onChange={e => setImportSearch(e.target.value)}
                      placeholder="z.B. Eschborn, Köln Classic …"
                      style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: 'var(--surface)', border: '1px solid rgba(59,73,75,0.4)', color: 'var(--on-surface)', padding: '6px 28px 6px 8px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                    />
                    {importSearch && (
                      <button onClick={() => setImportSearch('')}
                        style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--outline)', cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0 }}>
                        ✕
                      </button>
                    )}
                  </div>
                </div>
                <button onClick={handleSearch} disabled={importLoading}
                  style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', padding: '7px 16px', border: 'none', background: importLoading ? 'rgba(var(--brand-primary-rgb),0.3)' : 'var(--brand-primary)', color: importLoading ? 'rgba(0,0,0,0.4)' : '#0a1520', cursor: importLoading ? 'not-allowed' : 'pointer', flexShrink: 0 }}>
                  {importLoading ? '...' : 'SUCHEN'}
                </button>
                {filteredImportEvents.length > 0 && (
                  <button onClick={toggleSelectAll}
                    style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', padding: '7px 12px', border: '1px solid rgba(59,73,75,0.4)', background: 'transparent', color: 'var(--on-surface-variant)', cursor: 'pointer', flexShrink: 0 }}>
                    {filteredImportEvents.every(ev => importSelected.has(ev._origIdx)) ? 'Keine' : 'Alle'} wählen
                  </button>
                )}
              </div>
            </div>

            {/* Event list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem 0' }}>
              {importLoading ? (
                <div style={{ padding: '2rem', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--outline)' }}>
                  {importKlassen.size > 0 && importLv.size > 0
                    ? <>Lade Ausschreibungen mit Rennklassen...<br/><span style={{ fontSize: 9, color: 'rgba(59,73,75,0.6)', marginTop: 8, display: 'block' }}>Dauert ca. 15–30 Sek. (wird gecacht)</span></>
                    : <>Lade Ausschreibungen...<br/><span style={{ fontSize: 9, color: 'rgba(59,73,75,0.6)', marginTop: 8, display: 'block' }}>(ca. 18 Seiten, dauert ~10s)</span></>
                  }
                </div>
              ) : (() => {
                // Show hint if Rennklassen selected but no class data loaded yet
                const hasClassData = importEvents.some(ev => Array.isArray(ev.raceClasses) && ev.raceClasses.length > 0);
                const showClassHint = importKlassen.size > 0 && !hasClassData && importEvents.length > 0;
                if (filteredImportEvents.length === 0) {
                  return (
                    <div style={{ padding: '2rem', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--outline)' }}>
                      {importMsg || 'Keine zukünftigen Events gefunden. Filter anpassen.'}
                    </div>
                  );
                }
                return (
                  <>
                    {showClassHint && (
                      <div style={{ padding: '8px 1.25rem', background: 'rgba(255,180,0,0.06)', borderBottom: '1px solid rgba(255,180,0,0.15)', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'rgba(255,180,0,0.8)', letterSpacing: '0.06em' }}>
                        ⚠ Rennklassen-Filter benötigt Ausschreibungsdaten — Landesverband wählen &amp; SUCHEN klicken (zeige alle Events)
                      </div>
                    )}
                    {filteredImportEvents.map(ev => {
                const idx = ev._origIdx;
                const selected = importSelected.has(idx);
                const dateStr = ev.date ? new Date(ev.date + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                const dateEndStr = ev.dateEnd ? ` bis ${new Date(ev.dateEnd + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })}` : '';
                // Show only matching classes (when filter active), or hide if no filter
                const matchingClasses = importKlassen.size > 0 && ev.raceClasses
                  ? ev.raceClasses.filter(rc => rcMatchesKlassen(rc, [...importKlassen]))
                  : [];
                return (
                  <div key={idx} onClick={() => toggleImportSelect(idx)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 1.25rem', borderBottom: '1px solid rgba(59,73,75,0.12)', cursor: 'pointer', background: selected ? 'rgba(var(--brand-secondary-rgb),0.05)' : 'transparent', transition: 'background 0.1s' }}
                    onMouseEnter={e => { if (!selected) e.currentTarget.style.background = 'rgba(59,73,75,0.08)'; }}
                    onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}>
                    {/* Checkbox */}
                    <div style={{ width: 16, height: 16, border: `1px solid ${selected ? 'var(--brand-secondary)' : 'rgba(59,73,75,0.5)'}`, background: selected ? 'var(--brand-secondary)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.1s' }}>
                      {selected && <span style={{ fontSize: 10, color: '#111', fontWeight: 900, lineHeight: 1 }}>✓</span>}
                    </div>
                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: selected ? 'var(--brand-secondary)' : 'var(--on-surface)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.name}</div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <span>{dateStr}{dateEndStr}</span>
                        {ev.location && <span>· {ev.location}</span>}
                        {ev.lv && <span>· {ev.lv}</span>}
                        {ev.discipline && <span style={{ color: 'rgba(var(--brand-secondary-rgb),0.6)' }}>· {ev.discipline}</span>}
                      </div>
                      {/* Matching race class badges with start times */}
                      {matchingClasses.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
                          {matchingClasses.map((rc, i) => {
                            const cName = typeof rc === 'string' ? rc : (rc.className || '');
                            const cTime = typeof rc === 'object' ? rc.startTime : null;
                            const cDate = typeof rc === 'object' ? rc.date : null;
                            const showDate = cDate && ev.dateEnd; // multi-day: always show date
                            return (
                              <span key={i} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, fontWeight: 700, letterSpacing: '0.06em', padding: '2px 6px', background: 'rgba(var(--brand-secondary-rgb),0.1)', border: '1px solid rgba(var(--brand-secondary-rgb),0.35)', color: 'var(--brand-secondary)' }}>
                                {cName}
                                {showDate && cDate ? ` · ${new Date(cDate + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })}` : ''}
                                {cTime ? ` · ${cTime}` : ''}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {/* External link (Ausschreibung) */}
                    {ev.url && (
                      <a href={ev.url} target='_blank' rel='noopener noreferrer' onClick={e => e.stopPropagation()}
                        style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'var(--outline)', textDecoration: 'none', flexShrink: 0, border: '1px solid rgba(59,73,75,0.3)', padding: '3px 7px' }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--brand-secondary)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--outline)'}>
                        ↗
                      </a>
                    )}
                  </div>
                );
              })}
                  </>
                );
              })()}
            </div>

            {/* Footer */}
            {(() => {
              const selCount = filteredImportEvents.filter(ev => importSelected.has(ev._origIdx)).length;
              const canImport = selCount > 0 && !importing;
              return (
                <div style={{ padding: '0.875rem 1.25rem', borderTop: '1px solid rgba(59,73,75,0.3)', background: 'rgba(15,17,20,0.5)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: importMsg.includes('importiert') ? 'var(--brand-secondary)' : 'var(--outline)' }}>
                    {importMsg || (selCount > 0 ? `${selCount} EVENT${selCount !== 1 ? 'S' : ''} AUSGEWÄHLT` : 'Events auswählen und importieren')}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setShowImport(false)} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', padding: '8px 16px', border: '1px solid rgba(59,73,75,0.4)', background: 'transparent', color: 'var(--on-surface-variant)', cursor: 'pointer' }}>
                      SCHLIESSEN
                    </button>
                    <button onClick={handleImport} disabled={!canImport}
                      style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', padding: '8px 20px', border: 'none', background: canImport ? 'var(--brand-secondary)' : 'rgba(var(--brand-secondary-rgb),0.3)', color: canImport ? '#0d1200' : 'rgba(13,18,0,0.5)', cursor: canImport ? 'pointer' : 'not-allowed', transition: 'all 0.15s' }}>
                      {importing ? 'IMPORTIERE...' : `${selCount} IMPORTIEREN`}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

const ghostBtn = { fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', background: 'transparent', border: '1px solid rgba(59,73,75,0.4)', color: 'var(--on-surface-variant)', padding: '5px 10px', cursor: 'pointer', transition: 'all 0.15s' };

export default Events;
